import axios, { AxiosResponse } from "axios";
import ENV_CONFIG from "../config";
import { getAuthHeaders } from "../common";
import { IAcnowledgementResponse, ICreateDwTokenResponse, ICreateSharingRequest, ICreateSharingResponse, IDwToken, IExpireDwTokenResponse, ILicense, IPagedResult, IPasswordReset, ISharingCountResponse, ISharingRequest, ITransaction, ITransactionOwnersResponse } from "./models";

class InfraMarkerService {
   resetUrl = ENV_CONFIG.BASE_API_URL + '/api/v1/password-reset/';
   dwTokenUrl = ENV_CONFIG.BASE_API_URL + '/api/v1/dw-token/';
   dwSharingUrl = ENV_CONFIG.BASE_API_URL + '/api/v1/dw-sharing/';
   dwSharingPreviewUrl = ENV_CONFIG.BASE_API_URL + '/api/v1/dw-sharing/count/';

   generateDwToken(orgId: string, name: string, expiration: Date, geofilter: File | null, authToken: string, ownerFilter: string[], assetFilterType: string, assetFilterValue: string): Promise<AxiosResponse<ICreateDwTokenResponse>> {
      let formData = new FormData();
      formData.append("name", name);
      formData.append("org_id", orgId);
      formData.append("expiration_date", expiration.toISOString().split('T')[0]);
      if (geofilter !== null) {
         formData.append("geofilter", geofilter);
      }
      
      ownerFilter.forEach( o => formData.append("owner_filter", o));
      if (assetFilterValue) {
         formData.append("asset_filter_type", assetFilterType);
         formData.append("asset_filter_value", assetFilterValue);
      }

      // axios 1.x serializes FormData to JSON when Content-Type is application/json, dropping the File
      return axios.post(
         this.dwTokenUrl,
         formData,
         { headers: { ...getAuthHeaders(authToken).headers, 'Content-Type': 'multipart/form-data' } });
   }

   getGeofilter(url: string, authToken: string): Promise<AxiosResponse<Blob>> {
      return axios.get(url, { ...getAuthHeaders(authToken), responseType: 'blob' });
   }

   getDwTokens(authToken: string): Promise<AxiosResponse<IPagedResult<IDwToken>>> {
      return axios.get(this.dwTokenUrl, getAuthHeaders(authToken));
   }

   getDwTokenPage(pagedUrl: string, authToken: string): Promise<AxiosResponse<IPagedResult<IDwToken>>> {
      return axios.get(pagedUrl, getAuthHeaders(authToken));
   }

   expireDwToken(dwToken: string, authToken: string): Promise<AxiosResponse<IExpireDwTokenResponse>>  {
      return axios.put(this.dwTokenUrl + dwToken + '/', {}, getAuthHeaders(authToken));
   }

   createDataShare(dwToken: string, description: string, recipient: string, url: string, dataType: 'transaction' | 'inventory', authToken: string): Promise<AxiosResponse<ICreateSharingResponse>> {
      let payload: ICreateSharingRequest = { token: dwToken, description, recipient, url, data_type: dataType };
      return axios.post(this.dwSharingUrl, payload, getAuthHeaders(authToken));
   }

   getDataShares(authToken: string, url: string = this.dwSharingUrl): Promise<AxiosResponse<IPagedResult<ISharingRequest>>> {
      // Get data shares, default to sharing URL but allow overriding to support paging      
      return axios.get(url, getAuthHeaders(authToken));
   }

   getDataShareRecordCount(url: string, authToken: string): Promise<AxiosResponse<ISharingCountResponse>> {
      let payload = { url };
      return axios.post(this.dwSharingPreviewUrl, payload, getAuthHeaders(authToken));
   }

   expireDataShare(id: string, authToken: string) {
      return axios.put(this.dwSharingUrl + id + '/', {}, getAuthHeaders(authToken));
   } 

   resendDataShare(id: string, authToken: string) {
      return axios.post(this.dwSharingUrl, { id }, getAuthHeaders(authToken));
   }

   acknowledgeDataShare(id: string): Promise<AxiosResponse<IAcnowledgementResponse>>  {
      return axios.put(ENV_CONFIG.BASE_API_URL + '/api/v1/dw-sharing-acknowledge/', {id});
   }

   getTransactionOwners(org_id: string, authToken: string): Promise<AxiosResponse<ITransactionOwnersResponse>> {
      return axios.get(ENV_CONFIG.BASE_API_URL + '/api/v1/transaction-owners/?org_id=' + org_id, getAuthHeaders(authToken));
   }

   getLicense(orgId: string, authToken: string): Promise<AxiosResponse<ILicense>> {
      return axios.get(ENV_CONFIG.BASE_API_URL + '/api/v1/license/?org_id=' + orgId, getAuthHeaders(authToken));
   }

   getTransactionData(getTransactionUrl: string, authToken: string): Promise<AxiosResponse<IPagedResult<ITransaction>>> {
      return axios.get(getTransactionUrl, getAuthHeaders(authToken));
   }

   getTransactionExport(getTransactionExportUrl: string, authToken: string): Promise<AxiosResponse> {
      return axios.get(getTransactionExportUrl, getAuthHeaders(authToken));
   }

   submitPasswordResetRequest(email?: string, authToken?: string, recapValue?: string | null): Promise<AxiosResponse<IPasswordReset>> {
      if (email) {
         return axios.post(this.resetUrl, {email, recap_value: recapValue });
      } else {
         return axios.post(this.resetUrl, {},  getAuthHeaders(authToken));
      }
   }

   setPassword(password: string, token: string, userId?: string, email?: string, authToken?: string, recapValue?: string | null): Promise<AxiosResponse> {
      if (!userId && !email && !authToken) {
         throw new Error("You must either specify userId or email or have a token from an authenticated user to set the password.");
      }

      if (userId) {
         if (authToken) {
            return axios.put(this.resetUrl,  { pwd: password, token: token, user_id: userId, recap_value: recapValue }, getAuthHeaders(authToken))
         } else {
            return axios.put(this.resetUrl,  { pwd: password, token: token, user_id: userId, recap_value: recapValue })
         }
      } else if (email) {
         if (authToken) {
            return axios.put(this.resetUrl,  { pwd: password, token: token, email: email, recap_value: recapValue }, getAuthHeaders(authToken))
         } else {
            return axios.put(this.resetUrl,  { pwd: password, token: token, email: email, recap_value: recapValue })
         }
      } else {
         return axios.put(this.resetUrl,  { pwd: password, token: token }, getAuthHeaders(authToken))
      }
   }

   submitContactUs(email: string, username: string, authToken: string | null, supportText: string, recapValue: string | null) {
      const supportUrl = ENV_CONFIG.BASE_API_URL + '/api/v1/support/';

      if (authToken) {
         return axios.post(supportUrl, { support_text: supportText },  getAuthHeaders(authToken));
      } else {
         return axios.post(supportUrl, { email, username, support_text: supportText, recap_value: recapValue });
      }
   }
}

let inframarkerService = new InfraMarkerService();
export default inframarkerService